import { Dispatch } from 'redux';

import { DOWNLOAD_ERROR_STATUS } from 'shared/consts/consts';
import { IObjProps } from 'shared/consts/types';
import { downloadCSVFile, isValidNestedResponseObj, isValidResponseObject } from 'shared/helpers';

import { API } from 'store/Campaign/api';
import { DownloadAPI } from 'store/Download/api';

import { actions } from './store';

const { setCampaignsLoading, setCampaignData, setCampaignDataError } = actions;

export interface IApiResponse<T = any> {
	data: {
		responseObject?: T;
		response_obj?: {
			responseObject?: T;
		};
		[key: string]: any;
	};
	headers?: Record<string, string>;
	status?: number;
	statusText?: string;
}

export interface IVoiceCampaignExportResponse {
	exportId?: number | string;
	status?: string;
	fileToken?: string;
}

export interface IPaginatedResult<T = IObjProps> {
	content: T[];
	size: number;
	pageable: {
		pageNumber: number;
		pageSize?: number;
	};
	totalPages: number;
	totalElements: number;
}

export const getCampaignAction = (params: IObjProps) => (dispatch: Dispatch) => {
	dispatch(setCampaignsLoading(true));
	API.getCampaigns(params).then(
		(resp: IApiResponse<IPaginatedResult>) => {
			if (isValidNestedResponseObj(resp) && resp.data.response_obj?.responseObject) {
				dispatch(setCampaignData(getPaginationPayload(resp.data.response_obj.responseObject)));
			} else {
				dispatch(
					setCampaignDataError({
						isError: true,
						isLoading: false,
					})
				);
			}
		},
		() => {
			dispatch(
				setCampaignDataError({
					isError: true,
					isLoading: false,
				})
			);
		}
	);
};
export const getAnalyticsCampaignAction = (params: IObjProps) => (dispatch: Dispatch) => {
	dispatch(setCampaignsLoading(true));
	API.campaignAnalytics(params).then(
		(resp: IApiResponse<IPaginatedResult>) => {
			if (isValidResponseObject(resp) && resp.data.responseObject) {
				dispatch(setCampaignData(getPaginationPayload(resp.data.responseObject)));
			} else {
				dispatch(
					setCampaignDataError({
						isError: true,
						isLoading: false,
					})
				);
			}
		},
		() => {
			dispatch(
				setCampaignDataError({
					isError: true,
					isLoading: false,
				})
			);
		}
	);
};
const getPaginationPayload = (result: IPaginatedResult) => ({
	data: result?.content,
	pagination: {
		size: result.size,
		page: result.pageable.pageNumber + 1,
		totalPages: result.totalPages,
		totalElements: result.totalElements,
	},
	isError: false,
	isLoading: false,
});

export const pollForVoiceCampaignExportStatusAction =
	(exportId: number | string, fileName: string) => (dispatch: Dispatch) => {
		let count = 1;
		let isDownloadTriggered = false;

		const interval = setInterval(() => {
			if (count > 20) {
				clearInterval(interval);
			}

			API.getVoiceCampaignExportStatus(exportId).then(
				(response: IApiResponse<IVoiceCampaignExportResponse | string>) => {
					const responseObj = response?.data?.responseObject;
					const status =
						typeof responseObj === 'object' && responseObj !== null ? responseObj?.status : responseObj;
					const fileToken =
						typeof responseObj === 'object' && responseObj !== null ? responseObj?.fileToken : undefined;

					if (isValidResponseObject(response) && status === 'SUCCESS') {
						clearInterval(interval);
						if (!isDownloadTriggered) {
							isDownloadTriggered = true;
							dispatch(
								downloadVoiceCampaignExportFileAction(fileToken || String(exportId), fileName) as any
							);
						}
					} else if (status && DOWNLOAD_ERROR_STATUS.indexOf(status) > -1) {
						clearInterval(interval);
					}
					count++;
				}
			);
		}, 5000);
	};

export const downloadVoiceCampaignExportFileAction = (token: string, fileName: string) => () => {
	const params: IObjProps = {
		token,
	};
	DownloadAPI.getFileByToken(params).then((response: { data?: Blob | string; headers?: Record<string, string> }) => {
		const { data } = response;
		if (data) {
			const contentDisposition = response.headers?.['content-disposition'];
			let finalFileName = fileName;

			if (contentDisposition) {
				const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
				if (fileNameMatch && fileNameMatch[1]) {
					finalFileName = fileNameMatch[1];
				}
			}
			downloadCSVFile(data, finalFileName);
		}
	});
};

export const exportVoiceCampaignAction = (params: IObjProps, fileName: string) => (dispatch: Dispatch) =>
	API.exportVoiceCampaignData(params).then((resp: IApiResponse<IVoiceCampaignExportResponse | number | string>) => {
		if (isValidResponseObject(resp)) {
			const { responseObject } = resp.data;
			const exportId =
				typeof responseObject === 'object' && responseObject !== null
					? (responseObject.exportId as number) || Number(responseObject)
					: Number(responseObject);
			dispatch(pollForVoiceCampaignExportStatusAction(exportId, fileName) as any);

			return resp;
		}

		return Promise.reject(resp);
	});

export const exportVoiceCampaignRetargetGroupAction =
	(retargetGroupId: string, params: IObjProps = {}, fileName: string) =>
	(dispatch: Dispatch) =>
		API.exportVoiceCampaignRetargetGroupData(retargetGroupId, params).then(
			(resp: IApiResponse<IVoiceCampaignExportResponse | number | string>) => {
				if (isValidResponseObject(resp)) {
					const { responseObject } = resp.data;
					const exportId =
						typeof responseObject === 'object' && responseObject !== null
							? (responseObject.exportId as number) || Number(responseObject)
							: Number(responseObject);
					dispatch(pollForVoiceCampaignExportStatusAction(exportId, fileName) as any);

					return resp;
				}

				return Promise.reject(resp);
			}
		);
